import React from 'react';
import styled, { keyframes } from 'styled-components';
import Observer, {
  Props as ObserverProps,
} from '@researchgate/react-intersection-observer';
import getUrl from 'Jsx/Utils/getImgxUrl';

import { ImgxParams } from 'Jsx/Types/ImgxParams';

export interface LazyImageV2Props extends React.HTMLAttributes<HTMLDivElement> {
  children?: React.ReactNode;
  className?: string;
  pictureProps?: {
    className?: string;
  };
  bgColor: string;
  baseUrl: string;
  lazyUrl: string;
  imgxParams?: ImgxParams;
  alt: string;
  observerProps?: ObserverProps;
}

type LazyImageV2State = {
  isVisible: boolean;
};

const commonStyles = `
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const ImageTag = styled('img')<{ type?: string }>`
  ${commonStyles}
`;

const PictureAnimated = styled.picture`
  ${commonStyles};
  animation: ${fadeIn} 0.5s ease-in-out forwards;
`;

class LazyImageV2 extends React.Component<LazyImageV2Props, LazyImageV2State> {
  static defaultProps = {
    children: null,
    pictureProps: {},
    className: '',
    bgColor: '',
    baseUrl:
      'data:image/gif;base64,R0lGODlhAQABAIAAAP///wAAACH5BAEAAAAALAAAAAABAAEAAAICRAEAOw==',
    lazyUrl: '',
    imgxParams: {
      fit: 'crop',
    },
    alt: '',
    observerProps: {},
  };

  constructor(props) {
    super(props);
    this.state = {
      isVisible: false,
    };
    this.container = React.createRef();
  }

  container: React.Ref<any> = null;

  handleIntersection: ObserverProps['onChange'] = event => {
    const { isIntersecting } = event;

    // console.log({ isIntersecting, event, props: this.props });
    if (isIntersecting) {
      this.setState({ isVisible: isIntersecting });
    }
  };

  getQualityParam = ({
    imgxUrl,
    retina,
  }: {
    imgxUrl: string;
    retina: boolean;
  }): string => {
    const { imgxParams } = this.props;
    const imgxUrlValue = imgxUrl || '';
    const value = retina ? 75 : 85;
    const quality = imgxParams ? imgxParams.q || value : value;
    const qualityParam = !imgxUrlValue.includes('q=') ? `&q=${quality}` : '';

    return qualityParam;
  };

  render() {
    const {
      className,
      pictureProps,
      bgColor,
      baseUrl,
      lazyUrl,
      imgxParams,
      alt,
      observerProps,
      ...other
    } = this.props;
    const { isVisible } = this.state;

    const options = {
      ...observerProps,
      onChange: this.handleIntersection,
      rootMargin: '0px 0px 200px 0px',
      disabled: isVisible,
    };

    /*
      TODO: Add support for jpg images when no fm params is setted.
      We usually use jpg images.
    */
    // generate a img-url with the new imgx params
    const imgxUrl = getUrl<{ dpr: number }>(lazyUrl, {
      ...imgxParams,
      dpr: 2,
    });

    const qualityParam = this.getQualityParam({ imgxUrl, retina: false });
    // const qualityParamRetina = this.getQualityParam({ imgxUrl, retina: true });
    // generate a optimized image with webp format
    const optimizedImageUrl = `${imgxUrl}&fm=webp&lossless=false${qualityParam}`;
    // const optimizedImageRetina = `${imgxUrl}&fm=webp&lossless=true${qualityParamRetina}`;
    // reuse the same styles for differents components
    // const PictureAnimated = ImageTag.withComponent('picture');
    const Source = ImageTag.withComponent('source');

    return (
      <Observer {...options}>
        <div
          className={className}
          style={{
            width: '100%',
            height: '100%',
            position: 'relative',
            backgroundSize: '100% 100%',
            backgroundImage: `url(${baseUrl})`,
            backgroundColor: bgColor,
          }}
          {...other}
        >
          {isVisible ? (
            <PictureAnimated {...pictureProps}>
              <Source srcSet={optimizedImageUrl} type="image/webp" />
              <ImageTag alt={alt} src={imgxUrl} />
            </PictureAnimated>
          ) : (
            <div />
          )}
        </div>
      </Observer>
    );
  }
}

export default LazyImageV2;
